/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as Msdyn365 from '@msdyn365-commerce/core';
import { ILAC_AuthToken, ILAC_SuiteToValidateAdmin } from '../custom-actions/DataServiceEntities.g';
import { lAC_SuiteToValidateAdminAsync } from '../custom-actions/DataActionExtension.g';
import { refreshToken } from '../utils/authService';

/**
 * GetSuiteToValidateAdmin Input Action
 */
export class GetSuiteToValidateAdminInput implements Msdyn365.IActionInput {
    public authTokenCookie: ILAC_AuthToken;
    public eventId: number;
    public suiteId: number;

    constructor(authTokenCookie: ILAC_AuthToken, eventId: number, suiteId: number) {
        this.authTokenCookie = authTokenCookie;
        this.eventId = eventId;
        this.suiteId = suiteId;
    }

    // a cache object type and an appropriate cache key
    public getCacheKey = () => `GETSUITETOVALIDATEADMIN_${this.eventId}_${this.suiteId}`;
    public getCacheObjectType = () => 'GETSUITETOVALIDATEADMIN';
    public dataCacheType = (): Msdyn365.CacheType => 'application';
}

const isUnauthorized = (error: unknown): boolean =>
    (error as { data?: { ErrorResourceId?: string } } | undefined)?.data?.ErrorResourceId === '401';

/**
 * Throws when the suite could not be validated. A resolved value is written to the action cache under this
 * input's key, so answering with one on a failed call keeps the guest out of their suite for as long as that
 * entry lives, even once their session is good again. A rejection is not cached.
 */
async function getSuiteToValidateAdminAsync(
    input: GetSuiteToValidateAdminInput,
    ctx: Msdyn365.IActionContext
): Promise<ILAC_SuiteToValidateAdmin | undefined> {
    const authToken = input.authTokenCookie;

    if (!authToken?.IntuitDomeAccountId || !authToken.Token) {
        throw new Error('LAC_SuiteToValidateAdmin was asked for without an authenticated session');
    }

    try {
        return await lAC_SuiteToValidateAdminAsync({ callerContext: ctx }, authToken, input.eventId, input.suiteId);
    } catch (error) {
        if (!isUnauthorized(error)) {
            throw error;
        }

        const refreshedToken = await refreshToken(ctx);
        if (!refreshedToken) {
            throw error;
        }

        return lAC_SuiteToValidateAdminAsync(
            { callerContext: ctx },
            { IntuitDomeAccountId: refreshedToken.IntuitDomeAccountId, Token: refreshedToken.Token },
            input.eventId,
            input.suiteId
        );
    }
}

export default Msdyn365.createObservableDataAction({
    action: <Msdyn365.IAction<ILAC_SuiteToValidateAdmin | undefined>>getSuiteToValidateAdminAsync,
    id: 'GetSuiteToValidateAdmin'
});
